import React, { useRef } from 'react';
import { FaLinkedin, FaGithub, FaBars, FaTimes, FaSun, FaMoon } from 'react-icons/fa';
import { LuDownload } from 'react-icons/lu';
import mobileStyles from '../../styles/navigation/MobileNavbar.module.css';
import utilities from '../../styles/utilities.module.css';
import { getResumeUrl } from '../../utils/resumeUtils';
import { useTheme } from '../../hooks/useTheme';
import { useGlobalLayerState } from '../../hooks/useLayerState';

// Wrapper components to ensure proper JSX typing with React 19
const LinkedInIcon: React.FC = () => {
  const icon = FaLinkedin({});
  return icon as React.JSX.Element;
};
const GitHubIcon: React.FC = () => {
  const icon = FaGithub({});
  return icon as React.JSX.Element;
};
const BarsIcon: React.FC = () => {
  const icon = FaBars({});
  return icon as React.JSX.Element;
};
const TimesIcon: React.FC = () => {
  const icon = FaTimes({});
  return icon as React.JSX.Element;
};
const ResumeIcon: React.FC = () => {
  const icon = LuDownload({});
  return icon as React.JSX.Element;
};
const SunIcon: React.FC = () => {
  const icon = FaSun({});
  return icon as React.JSX.Element;
};
const MoonIcon: React.FC = () => {
  const icon = FaMoon({});
  return icon as React.JSX.Element;
};

interface NavBarProps {
    currentSection: string;
    onSectionChange: (section: string) => void;
}

const MobileNavbar: React.FC<NavBarProps> = ({ currentSection, onSectionChange }) => {
    // Theme management
    const { toggleTheme, isLight } = useTheme();

    // Layer state management for mobile menu
    const { showMobileMenu, showDefault, getCurrentPreset } = useGlobalLayerState();
    const isMenuOpen = getCurrentPreset() === 'MOBILE_MENU_ACTIVE';
    
    // Legacy state management (commented out for reference)
    // const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    // Update the current section when the element is clicked
    const navRef = useRef<HTMLElement>(null);
    const handleNavClick = (section: string, e: React.MouseEvent) => {
        e.preventDefault();
        const sectionElement = document.getElementById(section);
        if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        onSectionChange(section);
        
        // Close the menu after navigation
        showDefault();
        document.documentElement.style.setProperty('--navbar-lower-menu-display', `none`);
    };

    // Toggle the menu open/close state
    const toggleMenu = () => {
        if (isMenuOpen) {
            // Menu is open, so close it
            showDefault();
            document.documentElement.style.setProperty('--navbar-lower-menu-display', `none`);
        } else {
            // Menu is closed, so open it
            showMobileMenu();
            document.documentElement.style.setProperty('--navbar-lower-menu-display', `flex`);
        }
    };

    return (
        <nav className={`${mobileStyles.navbar} ${isMenuOpen ? mobileStyles.menuOpen : ''}`} ref={navRef}>
            <button className={`${mobileStyles.menuButton} ${utilities.interactivePadding}`} onClick={toggleMenu}>
                {isMenuOpen ? <TimesIcon /> : <BarsIcon />}
            </button>
            <div className={`${mobileStyles.pageName} ${utilities.interactivePadding}`}>{currentSection}</div>
            <ul className={`${mobileStyles.mobileNavbarItemHolder} ${isMenuOpen ? mobileStyles.showMenu : ''}`}>
                <li>
                    <a
                        onClick={(e) => handleNavClick('home', e)}
                        href="#home"
                        className={currentSection === 'home' ? mobileStyles.active : ''}
                    >
                        Home
                    </a>
                </li>
                <li>
                    <a
                        onClick={(e) => handleNavClick('projects', e)}
                        href="#projects"
                        className={currentSection === 'projects' ? mobileStyles.active : ''}
                    >
                        Projects
                    </a>
                </li>
                <li>
                    <a
                        onClick={(e) => handleNavClick('career', e)}
                        href="#career"
                        className={currentSection === 'career' ? mobileStyles.active : ''}
                    >
                        Career
                    </a>
                </li>
                <li className={mobileStyles.separator}>&nbsp;</li>
                <li>
                    <a
                        href="https://www.linkedin.com/in/benjaminxgregory"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkedInIcon /> LinkedIn
                    </a>
                </li>
                <li>
                    <a
                        href="https://github.com/TheShanachie"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <GitHubIcon /> GitHub
                    </a>
                </li>
                <li>
                    <a
                        href={getResumeUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <ResumeIcon /> Resume
                    </a>
                </li>
                <li className={mobileStyles.separator}>&nbsp;</li>
                <li>
                    <button
                        onClick={toggleTheme}
                        className={mobileStyles.themeToggle}
                        aria-label={`Switch to ${isLight ? 'dark' : 'light'} mode`}
                    >
                        {isLight ? <MoonIcon /> : <SunIcon />} 
                        {isLight ? 'Dark' : 'Light'}
                    </button>
                </li>
            </ul>
        </nav>
    );
}

export default MobileNavbar;
// This component is a placeholder for the mobile version of the navbar.